/* Mic Mode — one shared source of truth for every agent and both chat pages */
/* 'hf'  = hands-free (default): continuous listening, the platform detects the end of a turn  */
/* 'ptt' = Push-to-Talk: hold the mic to talk, or tap once to keep it open and tap again to send */
/* The choice is saved per user and device (not per agent) so it follows the user across agents. */

import { useCallback, useEffect, useRef, useState } from 'react';

export type MicMode = 'hf' | 'ptt';

const MIC_MODE_KEY = 'ibot_mic_mode';
export const MIC_MODE_EVENT = 'micModeChanged';

export function getMicMode(): MicMode {
  try {
    const saved = localStorage.getItem(MIC_MODE_KEY);
    if (saved === 'hf' || saved === 'ptt') return saved;
  } catch {
    // localStorage unavailable — fall through to the default
  }
  return 'hf';
}

export function setMicMode(mode: MicMode): void {
  try {
    localStorage.setItem(MIC_MODE_KEY, mode);
  } catch (e) {
    console.warn('[MicMode] localStorage write failed:', e);
  }
  window.dispatchEvent(new CustomEvent<MicMode>(MIC_MODE_EVENT, { detail: mode }));
}

/**
 * The turn_detection the page sends in session.update. The relay deletes the values (it applies its
 * own VAD) but READS the `handsfree` flag to pick the hands-free VAD tier: more sensitive, so the user
 * can barge in on the assistant (relay-server/lib/relayAPI.js, HANDS_FREE_VAD). Push-to-Talk keeps the
 * relay's default tier.
 */
export function getTurnDetection(mode: MicMode): Record<string, unknown> {
  if (mode === 'hf') return { type: 'server_vad', handsfree: true };
  return {
    type: 'server_vad',
    handsfree: false,
    eagerness: 'very_low', // reduce early cutoffs
    threshold: 0.82, // require clearer speech before auto-stop
    silence_duration_ms: 600, // VAD SILENCE WAIT: wait a bit longer before ending turn
    prefix_padding_ms: 500,
    create_response: false,
  };
}

/**
 * React hook used by both chat pages: current mode, a ref that always holds it, and live updates
 * when the Settings panel (or another tab) changes the choice.
 */
export function useMicMode() {
  const [micMode, setMicModeState] = useState<MicMode>(getMicMode);
  const micModeRef = useRef<MicMode>(micMode);

  useEffect(() => {
    micModeRef.current = micMode;
  }, [micMode]);

  useEffect(() => {
    const onChange = () => setMicModeState(getMicMode());
    window.addEventListener(MIC_MODE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(MIC_MODE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const changeMicMode = useCallback((mode: MicMode) => setMicMode(mode), []);

  return { micMode, micModeRef, changeMicMode };
}

/**
 * Hands-free keeps the microphone open, so the sound of typing and clicking reaches the voice detector,
 * which then "hears" a turn and the transcriber invents words for it (for example "Thank you for
 * watching" in another language). The pages use this to stop sending microphone audio for a moment after
 * any key press or click. Returns a function: true while that moment lasts.
 */
export function useInteractionNoiseGate(holdMs = 700) {
  const until = useRef(0);
  useEffect(() => {
    const bump = () => {
      until.current = Date.now() + holdMs;
    };
    const events = ['keydown', 'pointerdown', 'mousedown', 'touchstart'];
    events.forEach((e) => document.addEventListener(e, bump, true));
    return () => events.forEach((e) => document.removeEventListener(e, bump, true));
  }, [holdMs]);
  return useCallback(() => Date.now() < until.current, []);
}
